import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { MODULE_CATALOG } from '@vce/shared';
import { PageHeader } from '@/app/layout';
import { useTenantReady } from '@/app/tenant-context';
import { NoTenantState } from '@/components/no-tenant-state';
import { PaginationButtons } from '@/components/pagination-buttons';
import {
  Badge,
  Card,
  EmptyState,
  Input,
  QueryState,
  ScoreBadge,
  Select,
  Spinner,
} from '@/components/ui/primitives';
import { api } from '@/lib/api';
import { useRunsModuleCta } from '@/lib/module-cta';
import { formatNumber, formatRelative } from '@/lib/utils';
import { ProductDrawer } from '@/components/product-drawer';
import { ProductThumb } from '@/components/product-thumb';

const PAGE_SIZE = 30;

export function ProductsPage() {
  const { tenantId, isResolving } = useTenantReady();
  const runsCta = useRunsModuleCta();
  const [params, setParams] = useSearchParams();
  const searchParam = params.get('search') ?? '';
  const [searchInput, setSearchInput] = useState(searchParam);
  const [search, setSearch] = useState(searchParam);
  const [page, setPage] = useState(1);

  const categoryId = params.get('categoryId') ?? '';
  const ruleId = params.get('ruleId') ?? '';
  const sort = params.get('sort') ?? 'score';
  const productIdRaw = params.get('productId');
  const openProductId =
    productIdRaw && Number.isFinite(Number(productIdRaw)) ? Number(productIdRaw) : null;

  useEffect(() => {
    setSearchInput(searchParam);
    setSearch(searchParam);
    setPage(1);
  }, [searchParam]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const query = useQuery({
    queryKey: ['products', tenantId, search, categoryId, ruleId, sort, page],
    queryFn: () =>
      api.catalog.products(tenantId as string, {
        search: search || undefined,
        categoryId: categoryId || undefined,
        ruleId: ruleId || undefined,
        sort,
        order: sort === 'score' ? 'asc' : 'desc',
        page,
        pageSize: PAGE_SIZE,
      }),
    enabled: tenantId !== null,
  });

  if (isResolving) {
    return <Spinner label="Carregando conta…" />;
  }

  if (!tenantId) {
    return <NoTenantState />;
  }

  const total = query.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const items = query.data?.items ?? [];

  function updateParam(key: string, value: string): void {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
    setPage(1);
  }

  function openProduct(productId: number): void {
    const next = new URLSearchParams(params);
    next.set('productId', String(productId));
    setParams(next, { replace: true });
  }

  function closeProduct(): void {
    const next = new URLSearchParams(params);
    next.delete('productId');
    setParams(next, { replace: true });
  }

  return (
    <>
      <PageHeader
        title={MODULE_CATALOG.products.label}
        description={`${formatNumber(total)} produtos no snapshot local. A busca é sobre o snapshot, não sobre o catálogo ao vivo.`}
        actions={
          <>
            <div className="relative w-full sm:w-auto sm:min-w-[16rem] sm:flex-1 md:flex-none">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-[var(--color-muted)]" />
              <Input
                className="w-full pl-8"
                placeholder="Nome, marca ou ID"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
              />
            </div>
            <Select
              className="w-full sm:w-auto"
              value={sort}
              onChange={(event) => updateParam('sort', event.target.value)}
            >
              <option value="score">Pior score primeiro</option>
              <option value="findings">Mais problemas</option>
              <option value="name">Nome</option>
              <option value="updatedAt">Atualizado recentemente</option>
            </Select>
          </>
        }
      />

      <div className="space-y-3 p-4 sm:p-6">
        {ruleId || categoryId ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-[var(--color-muted)]">Filtros:</span>
            {ruleId ? (
              <FilterChip label={`regra ${ruleId}`} onClear={() => updateParam('ruleId', '')} />
            ) : null}
            {categoryId ? (
              <FilterChip
                label={`categoria ${categoryId}`}
                onClear={() => updateParam('categoryId', '')}
              />
            ) : null}
          </div>
        ) : null}

        <Card>
          <QueryState
            isLoading={query.isLoading}
            isError={query.isError}
            errorTitle="Não foi possível carregar os produtos"
            onRetry={() => void query.refetch()}
          >
            {items.length === 0 ? (
              <EmptyState
                title="Nenhum produto encontrado"
                description={runsCta.description('Ajuste os filtros ou rode uma ingestão.')}
                action={runsCta.action()}
              />
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full text-sm">
                    <thead className="bg-[var(--color-surface-soft)] text-left text-xs text-[var(--color-muted)]">
                      <tr>
                        <th className="px-5 py-2 font-medium">Produto</th>
                        <th className="px-5 py-2 font-medium">Categoria</th>
                        <th className="px-5 py-2 text-right font-medium">SKUs</th>
                        <th className="px-5 py-2 text-right font-medium">Problemas</th>
                        <th className="px-5 py-2 text-right font-medium">Pendentes</th>
                        <th className="px-5 py-2 text-right font-medium">Score</th>
                        <th className="px-5 py-2 text-right font-medium">Ingerido</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--color-hairline)]">
                      {items.map((item) => (
                        <tr
                          key={item.productId}
                          className="cursor-pointer hover:bg-[var(--color-surface-elevated)]"
                          onClick={() => openProduct(item.productId)}
                        >
                          <td className="px-5 py-2.5">
                            <div className="flex items-center gap-3">
                              <ProductThumb src={item.imageUrl} alt={item.name} />
                              <div className="min-w-0">
                                <p className="font-medium text-[var(--color-ink)]">{item.name}</p>
                                <p className="text-xs text-[var(--color-muted)]">
                                  #{item.productId} · {item.brandName ?? 'sem marca'}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-2.5 text-xs text-[var(--color-body)]">
                            {item.categoryName ?? '—'}
                          </td>
                          <td className="px-5 py-2.5 text-right tabular-nums">{item.skuCount}</td>
                          <td className="px-5 py-2.5 text-right tabular-nums">
                            {item.findingCount}
                            {item.criticalCount > 0 ? (
                              <Badge tone="critical" className="ml-1">
                                {item.criticalCount}
                              </Badge>
                            ) : null}
                          </td>
                          <td className="px-5 py-2.5 text-right tabular-nums">
                            {item.pendingSuggestionCount > 0 ? (
                              <Badge tone="brand">{item.pendingSuggestionCount}</Badge>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="px-5 py-2.5 text-right">
                            <ScoreBadge score={item.score} />
                          </td>
                          <td className="px-5 py-2.5 text-right text-xs text-[var(--color-muted)]">
                            {formatRelative(item.fetchedAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="divide-y divide-[var(--color-hairline)] md:hidden">
                  {items.map((item) => (
                    <button
                      key={item.productId}
                      type="button"
                      className="flex w-full flex-col gap-1.5 px-4 py-3 text-left hover:bg-[var(--color-surface-elevated)]"
                      onClick={() => openProduct(item.productId)}
                    >
                      <div className="flex items-start gap-3">
                        <ProductThumb src={item.imageUrl} alt={item.name} />
                        <div className="min-w-0 flex-1 space-y-1.5">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-medium text-[var(--color-ink)]">{item.name}</p>
                            <ScoreBadge score={item.score} />
                          </div>
                          <p className="text-xs text-[var(--color-muted)]">
                            #{item.productId} · {item.brandName ?? 'sem marca'} ·{' '}
                            {item.categoryName ?? 'sem categoria'}
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            <Badge tone="neutral">{item.skuCount} SKUs</Badge>
                            <Badge tone="neutral">{item.findingCount} problemas</Badge>
                            {item.pendingSuggestionCount > 0 ? (
                              <Badge tone="brand">{item.pendingSuggestionCount} pendentes</Badge>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </QueryState>
        </Card>

        <PaginationButtons
          className="justify-end"
          page={page}
          pageCount={pageCount}
          onPrev={() => setPage((p) => p - 1)}
          onNext={() => setPage((p) => p + 1)}
        />
      </div>

      {openProductId !== null ? (
        <ProductDrawer
          tenantId={tenantId}
          productId={openProductId}
          onClose={closeProduct}
        />
      ) : null}
    </>
  );
}

function FilterChip({ label, onClear }: { label: string; onClear(): void }) {
  return (
    <button
      type="button"
      onClick={onClear}
      className="inline-flex items-center gap-1 rounded-full bg-[var(--color-brand-soft)] px-2.5 py-0.5 text-[11px] text-[var(--color-primary)]"
    >
      {label}
      <span aria-hidden>×</span>
    </button>
  );
}
